import { useCallback, useState } from "react";

export interface Preferences {
  audio: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  touchOffset: number;
}

const STORAGE_KEY = "air-hockey.preferences.v1";

const DEFAULT_PREFERENCES: Preferences = {
  audio: true,
  reducedMotion: false,
  highContrast: false,
  touchOffset: 0.42,
};

export function usePreferences(): [Preferences, (patch: Partial<Preferences>) => void] {
  const [preferences, setPreferences] = useState<Preferences>(readPreferences);
  const updatePreferences = useCallback((patch: Partial<Preferences>) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Preferences remain available for this session when storage is blocked.
      }
      return next;
    });
  }, []);
  return [preferences, updatePreferences];
}

function readPreferences(): Preferences {
  const systemReducedMotion = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...DEFAULT_PREFERENCES, reducedMotion: systemReducedMotion };
    const parsed = JSON.parse(stored) as Partial<Preferences>;
    return {
      audio: typeof parsed.audio === "boolean" ? parsed.audio : DEFAULT_PREFERENCES.audio,
      reducedMotion: typeof parsed.reducedMotion === "boolean" ? parsed.reducedMotion : systemReducedMotion,
      highContrast: typeof parsed.highContrast === "boolean" ? parsed.highContrast : DEFAULT_PREFERENCES.highContrast,
      touchOffset: typeof parsed.touchOffset === "number" ? Math.min(1, Math.max(0, parsed.touchOffset)) : DEFAULT_PREFERENCES.touchOffset,
    };
  } catch {
    return { ...DEFAULT_PREFERENCES, reducedMotion: systemReducedMotion };
  }
}
